import { useMemo } from 'react';

import { a, useSpring } from '@react-spring/web';
import { styled } from '@spazious/dollhouse-theme';
import { Flex, FlexProps } from '@spazious/dollhouse-uikit';
import { useMeasure } from '@spazious/react-hooks';

import { useDeviceInfo } from '../../hooks/useDeviceInfo.js';
import { CloseButton } from '../CloseButton/index.js';

interface InnerPanelProps {
  isPortrait: boolean;
}

const AnimatedFlex = a(Flex);

const InnerPanel = styled(Flex)<InnerPanelProps>`
  flex: 1;
  overflow-y: scroll;
  ${({ isPortrait }) =>
    isPortrait
      ? `
    max-height: 40dvh;
    min-width: 20dvw;
  `
      : `
    max-width: 50dvw;
    border-right: 1px solid var(--spz-sys-color-surface-container);
  `};
`;

export interface SidePanelMobileProps extends FlexProps {
  open?: boolean;
  width?: number;
  onClose?: () => void;
}

export const SidePanelMobile = styled(
  ({
    open,
    onClose,
    width: widthProp,
    height: heightProp,
    children,
    ...restProps
  }: SidePanelMobileProps) => {
    const [setElemetRef, { width, height }] = useMeasure();
    const defaultWidth = widthProp ?? width;
    const defaultHeight = heightProp ?? height;
    const isPortrait = useDeviceInfo((state) => state.isPortrait);

    const widthStyle = useSpring({
      from: { width: 0 },
      to: { width: open ? defaultWidth : 0 },
    });

    const heightStyle = useSpring({
      from: { height: 0 },
      to: { height: open ? defaultHeight : 0 },
    });

    const style = useMemo(
      () => ({
        [isPortrait ? 'height' : 'width']: isPortrait
          ? heightStyle.height.to((value) => `${value}px`)
          : widthStyle.width.to((value) => `${value}px`),
      }),
      [heightStyle.height, isPortrait, widthStyle.width]
    );

    const innerPanelWidth = isPortrait ? '100dvw' : 'fit-content';
    const innerPanelHeight = isPortrait ? '100%' : '100dvh';

    return (
      <AnimatedFlex style={style} {...restProps}>
        <CloseButton onClick={onClose} />
        <InnerPanel
          ref={setElemetRef as unknown as React.RefCallback<HTMLElement>}
          height={innerPanelHeight}
          isPortrait={isPortrait}
          width={innerPanelWidth}
        >
          {children}
        </InnerPanel>
      </AnimatedFlex>
    );
  }
).withConfig({
  displayName: 'SidePanelMobile',
})`
  position: relative;
  overflow: hidden;
  background-color: var(--spz-sys-color-surface-variant);
  backdrop-filter: opacity(0.5) blur(10px);
  box-shadow: var(--spz-sys-color-surface-container-high) 1px 0px 2px 0px;
  flex: 1;
`;
